import React from 'react';
import { Building2, GraduationCap, Shield, Briefcase } from 'lucide-react';
import { CyberGrid } from '../components/CyberGrid';

const Industries: React.FC = () => {
  const industries = [
    {
      icon: Building2,
      title: 'Finance',
      description:
        'Protect sensitive financial data and maintain regulatory compliance with security programs designed for high-risk environments.',
    },
    {
      icon: GraduationCap,
      title: 'Education',
      description:
        'Secure student data, research systems, and campus infrastructure while supporting academic and operational needs.',
    },
    {
      icon: Shield,
      title: 'Government',
      description:
        'Meet stringent security requirements and compliance standards with repeatable, audit-friendly vulnerability processes.',
    },
    {
      icon: Briefcase,
      title: 'SMBs',
      description:
        'Affordable, scalable security services built for small and medium businesses—clear priorities and measurable outcomes.',
    },
  ];

  return (
    <div className="w-full">
      {/* HERO — match Home/About/Services */}
      <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white py-12 md:py-16 overflow-hidden">
        <CyberGrid tone="dark" animate opacity={0.08} wash={false} className="z-0" />

        {/* Soft spotlights (static) */}
        <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -top-24 left-10 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-[520px] h-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-slate-200 text-sm font-semibold backdrop-blur-sm mb-4">
            <Shield size={16} className="text-cyan-300" />
            <span>WHO WE SERVE</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-white mb-6">
            Industries We Serve
          </h1>

          <p className="text-xl text-slate-300 max-w-3xl">
            Cybersecurity services tailored to your industry’s compliance demands, threat landscape, and operational realities.
          </p>
        </div>
      </section>

      {/* CONTENT — light section with subtle grid wash */}
      <section className="relative py-12 md:py-16 bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60 overflow-hidden">
        <CyberGrid opacity={0.05} wash className="z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white/90 backdrop-blur-sm p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 hover:border-slate-300 overflow-hidden"
                >
                  {/* subtle corner glow */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-slate-900 to-indigo-500 opacity-[0.05] rounded-full -mr-20 -mt-20" />

                  <div className="relative">
                    <div className="flex items-center gap-5 mb-6">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center shadow-lg">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-extrabold text-slate-900">{industry.title}</h3>
                    </div>

                    <p className="text-slate-600 leading-relaxed text-lg">{industry.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
